import React, { useState } from 'react';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';

const PopoverShell = ({ open, anchorEl, onClose, title, accent, children, copyText }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!copyText) return;
    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={() => { onClose(); setCopied(false); }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { maxWidth: 420, borderRadius: 1 } } }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: accent }}>
            {title}
          </Typography>
          <IconButton size="small" onClick={handleCopy} sx={{ ml: 1 }}>
            {copied
              ? <CheckIcon sx={{ fontSize: 16, color: '#00C853' }} />
              : <ContentCopyIcon sx={{ fontSize: 16, color: 'rgba(0,0,0,0.4)' }} />}
          </IconButton>
        </Box>
        <Box sx={{ fontSize: 13, lineHeight: 1.6, color: 'rgba(0,0,0,0.75)', overflowY: 'auto', maxHeight: 300 }}>
          {children}
        </Box>
      </Box>
    </Popover>
  );
};

const renderDocsContent = (text) => {
  return text.split('\n').map((line, i) => {
    const isSectionHeader = /^\[.+\]$/.test(line.trim());
    const isLabel = /^(Personal|Business|Address):/.test(line.trim());
    const isBullet = line.trim().startsWith('•');
    if (!line.trim()) return <Box key={i} sx={{ height: 8 }} />;
    if (isSectionHeader) return null;
    return (
      <Box key={i} sx={{
        mb: 0.2,
        pl: isBullet ? 2 : 0,
        fontWeight: isLabel ? 600 : 400,
        color: isLabel ? 'rgba(0,0,0,0.87)' : 'rgba(0,0,0,0.7)',
      }}>
        {line}
      </Box>
    );
  });
};

const DocsNotePopovers = ({ docs, note }) => {
  const { t } = useTranslation();

  return (
    <>
      <PopoverShell
        open={Boolean(docs?.anchorEl)}
        anchorEl={docs?.anchorEl}
        onClose={docs?.onClose || (() => {})}
        title={t('calculator.requiredDocs', 'Required documents')}
        accent="#1976d2"
        copyText={docs?.text}
      >
        {docs?.text ? renderDocsContent(docs.text) : null}
      </PopoverShell>

      <PopoverShell
        open={Boolean(note?.anchorEl)}
        anchorEl={note?.anchorEl}
        onClose={note?.onClose || (() => {})}
        title={t('calculator.commentTitle', 'Note')}
        accent="#1976d2"
        copyText={note?.text}
      >
        {note?.text ? (
          <Typography sx={{ fontSize: 13, whiteSpace: 'pre-wrap', color: 'rgba(0,0,0,0.75)' }}>
            {note.text}
          </Typography>
        ) : null}
      </PopoverShell>
    </>
  );
};

export default DocsNotePopovers;
